import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowDownToLine } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtQty } from "./stockUtils";

const today = () => new Date().toISOString().slice(0, 10);

// Entrada = SEMPRE um novo lote do produto (validade + etiqueta próprias).
export function LotEntryDialog({ product, onOpenChange, onCreated, userName }) {
  const open = !!product;
  const [form, setForm] = useState({ quantity: "", production_date: "", shelf_life_days: "", batch: "", reason: "", responsible: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        quantity: "",
        production_date: today(),
        shelf_life_days: product.shelf_life_days ?? "",
        batch: "",
        reason: "",
        responsible: userName || "",
      });
    }
  }, [product, userName]);

  const expiry = useMemo(() => {
    if (!form.production_date || form.shelf_life_days === "") return "";
    const d = new Date(form.production_date + "T00:00:00");
    d.setDate(d.getDate() + Number(form.shelf_life_days));
    return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  }, [form.production_date, form.shelf_life_days]);

  const submit = async () => {
    if (!form.quantity || Number(form.quantity) <= 0) {
      toast.error("Quantidade deve ser maior que 0");
      return;
    }
    setSaving(true);
    try {
      const { data: lot } = await api.post(`/sanitary/products/${product.id}/lots`, {
        quantity: Number(form.quantity),
        production_date: form.production_date || null,
        shelf_life_days: form.shelf_life_days === "" ? null : Number(form.shelf_life_days),
        batch: form.batch || null,
        responsible: form.responsible || null,
        reason: form.reason,
      });
      toast.success(`Lote registrado — ${fmtQty(lot.quantity)} ${lot.unit} de ${product.name}`);
      onOpenChange(false);
      onCreated?.(lot);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao registrar entrada");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent data-testid="lot-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ArrowDownToLine className="h-5 w-5 text-emerald-800" /> Entrada — {product?.name}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            Toda entrada cria um <b>novo lote</b> dentro do produto, com validade e etiqueta QR próprias.
            Saldo atual: <b>{fmtQty(product?.quantity)} {product?.unit}</b> em {product?.lots_count || 0} lote(s).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Quantidade ({product?.unit}) *</Label>
              <Input type="number" step="0.01" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 bg-white" data-testid="lot-qty" autoFocus />
            </div>
            <div>
              <Label>Nº do lote</Label>
              <Input value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })} placeholder="opcional" className="mt-1 bg-white" data-testid="lot-batch" />
            </div>
            <div>
              <Label>Data de produção</Label>
              <Input type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} className="mt-1 bg-white" data-testid="lot-production-date" />
            </div>
            <div>
              <Label>Validade (dias)</Label>
              <Input type="number" min="0" value={form.shelf_life_days} onChange={(e) => setForm({ ...form, shelf_life_days: e.target.value })} className="mt-1 bg-white" data-testid="lot-shelf-days" />
            </div>
          </div>
          <div className="rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-xs font-body text-stone-700" data-testid="lot-expiry-hint">
            {expiry ? <>Validade do lote: <b>{fmtDate(expiry)}</b></> : "Sem validade calculada — informe produção e dias."}
          </div>
          <div>
            <Label>Responsável pela produção</Label>
            <Input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} placeholder="Nome de quem produziu / recebeu" className="mt-1 bg-white" data-testid="lot-responsible" />
          </div>
          <div>
            <Label>Motivo / origem</Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Recebimento do fornecedor, produção do turno…" className="mt-1 bg-white" data-testid="lot-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="lot-submit">
            Registrar entrada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
